<template>
  <div class="flex flex-col gap-5">
    <!-- When it was sent, segment filter, cache state, refresh -->
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div style="color:var(--muted);">
        <template v-if="campaignData">
          {{ campaignData.campaign.send_time ? `Sent ${formatDate(campaignData.campaign.send_time)}` : 'Not sent yet' }}
        </template>
      </div>
      <div class="flex flex-wrap items-center gap-3">
        <USelect
          v-if="segmentOptions.length"
          :model-value="selectedSegmentId ?? undefined"
          :items="segmentOptions"
          placeholder="All segments"
          aria-label="Segment"
          class="min-w-[200px]"
          :loading="loadingSegmentData"
          @update:model-value="onSegmentChange"
        />
        <span v-if="cacheStatus.hit" style="color:var(--muted); font-size:13px;">
          {{ cacheStatus.stale ? 'Updating now' : `Updated ${ageText}` }}
        </span>
        <AppButton :disabled="refreshingData" @click="refreshData">
          <UIcon name="i-heroicons-arrow-path" class="h-4 w-4" :class="{ 'animate-spin': refreshingData }" />
          Refresh
        </AppButton>
      </div>
    </div>

    <!-- Segment filter notice -->
    <div
      v-if="selectedSegmentId && campaignData"
      class="console-card flex flex-wrap items-center justify-between gap-3"
      style="padding:12px 18px;"
    >
      <div>
        <span style="color:var(--muted);">Showing one segment, </span>
        <span style="font-weight:600;">{{ getSegmentName(selectedSegmentId) }}</span>
        <div v-if="!loading && !hasSegmentData" style="color:var(--warn);">
          Your email platform returned no figures for this segment. The numbers below are for the whole send.
        </div>
      </div>
      <AppButton small @click="clearSegmentFilter">Show everyone</AppButton>
    </div>

    <AppLoadingState
      v-if="loading"
      variant="kpi"
      :count="6"
      :label="selectedSegmentId ? 'Loading this segment' : 'Loading results from your email platform'"
      slow-hint="Your email platform can take a few seconds to answer."
    />

    <template v-else-if="campaignData">
      <!-- Campaign details -->
      <div v-if="!hideLinkedEmail" class="console-card">
        <h2 class="ca-head">Campaign</h2>
        <dl class="ca-facts">
          <div>
            <dt>Subject</dt>
            <dd>{{ campaignData.campaign.subject_line }}</dd>
          </div>
          <div>
            <dt>Preview text</dt>
            <dd>{{ campaignData.campaign.preview_text || 'None' }}</dd>
          </div>
          <div>
            <dt>Audience</dt>
            <dd class="flex flex-wrap items-center gap-2">
              <span>{{ campaignData.campaign.list_name || 'Unknown audience' }}</span>
              <ConsoleBadge :color="campaignData.campaign.list_is_active ? 'good' : 'bad'">
                {{ campaignData.campaign.list_is_active ? 'Active' : 'Inactive' }}
              </ConsoleBadge>
            </dd>
          </div>
          <div v-if="campaignData.email">
            <dt>Email</dt>
            <dd>
              <NuxtLink :to="`/app/emails/${groupAlias}/${campaignData.email.alias}`" class="ca-link">
                {{ campaignData.email.name || campaignData.email.alias }}
              </NuxtLink>
            </dd>
          </div>
        </dl>
      </div>

      <!-- Headline numbers -->
      <div class="ca-kpis">
        <ConsoleKpiCard
          v-for="k in kpis"
          :key="k.label"
          :label="k.label"
          :value="k.value"
          :delta="k.delta"
          :delta-color="k.color"
          value-size="28px"
        />
      </div>

      <div class="ca-pairs">
        <div class="console-card kpi">
          <div class="console-label">{{ hasSegmentData ? 'Segment against the whole send' : 'Industry average' }}</div>
          <div v-if="!hasSegmentData" class="ca-pair">
            <div>
              <div class="ca-num display mono">{{ formatPercent(campaignData.campaign.industry_stats?.open_rate || 0) }}</div>
              <div class="console-label">Open rate</div>
            </div>
            <div>
              <div class="ca-num display mono">{{ formatPercent(campaignData.campaign.industry_stats?.click_rate || 0) }}</div>
              <div class="console-label">Click rate</div>
            </div>
          </div>
          <div v-else class="ca-pair">
            <div v-for="c in comparison" :key="c.label">
              <div class="ca-num display mono flex items-center gap-1" :style="{ color: c.up ? 'var(--good)' : 'var(--bad)' }">
                <UIcon v-if="c.up" name="i-heroicons-arrow-up" class="h-4 w-4" />
                <UIcon v-else name="i-heroicons-arrow-down" class="h-4 w-4" />
                {{ c.value }}
              </div>
              <div class="console-label">{{ c.label }}</div>
            </div>
          </div>
          <div v-if="!hasSegmentData" class="kpi-delta" style="color:var(--muted);">
            {{ campaignData.campaign.industry_stats?.type || 'No industry figures for this account' }}
          </div>
        </div>

        <div class="console-card kpi">
          <div class="console-label">Latest activity</div>
          <div class="ca-pair">
            <div>
              <div class="ca-num ca-num--date">{{ campaignData.campaign.opens.last_open ? formatDate(campaignData.campaign.opens.last_open, true) : 'None' }}</div>
              <div class="console-label">Last open</div>
            </div>
            <div>
              <div class="ca-num ca-num--date">{{ campaignData.campaign.clicks.last_click ? formatDate(campaignData.campaign.clicks.last_click, true) : 'None' }}</div>
              <div class="console-label">Last click</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Links -->
      <div class="console-card">
        <h2 class="ca-head">Most-clicked links</h2>
        <p v-if="!topLinks.length" class="ca-empty">Nobody has clicked a link yet.</p>
        <div v-else>
          <div v-for="url in topLinks" :key="url.url" class="console-row ca-linkrow">
            <div class="flex items-center justify-between gap-4">
              <a :href="url.url" target="_blank" rel="noopener" class="ca-link truncate" :title="url.url">{{ truncateUrl(url.url) }}</a>
              <div class="mono" style="white-space:nowrap;">
                <span style="font-weight:600;">{{ formatNumber(url.total_clicks) }}</span>
                <span style="color:var(--muted);"> clicks, {{ formatNumber(url.unique_clicks) }} unique, {{ formatPercent(url.click_percentage) }}</span>
              </div>
            </div>
            <div class="ca-bar">
              <div :style="{ width: (maxLinkClicks ? (url.total_clicks || 0) / maxLinkClicks * 100 : 0) + '%' }" />
            </div>
          </div>
        </div>
      </div>

      <!-- Domains -->
      <div class="console-card">
        <h2 class="ca-head">Recipient domains</h2>
        <p v-if="!topDomains.length" class="ca-empty">No domain figures for this campaign.</p>
        <div v-else style="overflow-x:auto;">
          <table class="ca-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Recipients</th>
                <th>Opens</th>
                <th>Clicks</th>
                <th>Bounces</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="domain in topDomains" :key="domain.domain" class="console-row">
                <td style="font-weight:600;">{{ domain.domain }}</td>
                <td class="mono">{{ formatNumber(domain.emails_sent) }} <span>{{ formatPercent(domain.emails_pct) }}</span></td>
                <td class="mono">{{ formatNumber(domain.opens) }} <span>{{ formatPercent(domain.opens_pct) }}</span></td>
                <td class="mono">{{ formatNumber(domain.clicks) }} <span>{{ formatPercent(domain.clicks_pct) }}</span></td>
                <td class="mono">{{ formatNumber(domain.bounces) }} <span>{{ formatPercent(domain.bounces_pct) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <div v-else-if="error" class="console-card" role="alert" style="padding:24px;">
      <div style="font-weight:600; color:var(--bad);">The results did not load</div>
      <p style="margin:6px 0 0; color:var(--muted);">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'

const props = withDefaults(
  defineProps<{
    campaignId: string | number
    groupAlias: string
    /** hide the campaign-details card + linked-email row (redundant when embedded in the email view) */
    hideLinkedEmail?: boolean
  }>(),
  { hideLinkedEmail: false },
)

const emit = defineEmits<{ loaded: [any] }>()

const loading = ref(true)
const error = ref<string | null>(null)
const campaignData = ref<any>(null)
const cacheStatus = ref<{ hit: boolean; age: number; stale: boolean; fresh?: boolean }>({ hit: false, age: 0, stale: false })
const refreshingData = ref(false)
const selectedSegmentId = ref<string | null>(null)
const loadingSegmentData = ref(false)

const segmentOptions = computed(() => {
  if (!campaignData.value || !campaignData.value.segments) return []
  return campaignData.value.segments.map((segment: any) => ({
    label: `${segment.name}, ${segment.member_count} people`,
    value: segment.id,
  }))
})

const hasSegmentData = computed(() => !!selectedSegmentId.value && !!campaignData.value?.segment_data)

type Kpi = { label: string; value: string; delta?: string; color?: 'good' | 'bad' | 'warn' | 'muted' }

// The headline tiles. With a segment selected they show that segment and colour
// opens and clicks by whether the segment beat the whole send.
const kpis = computed<Kpi[]>(() => {
  const c = campaignData.value?.campaign
  if (!c) return []
  const s = hasSegmentData.value ? campaignData.value.segment_data : null
  if (!s) {
    return [
      { label: 'Sent', value: formatNumber(c.emails_sent) },
      { label: 'Opens', value: formatNumber(c.opens.opens_total), delta: `${formatNumber(c.opens.unique_opens)} unique, ${formatPercent(c.opens.open_rate)}`, color: 'good' },
      { label: 'Clicks', value: formatNumber(c.clicks.clicks_total), delta: `${formatNumber(c.clicks.unique_clicks)} unique, ${formatPercent(c.clicks.click_rate)}`, color: 'good' },
      { label: 'Bounces', value: formatNumber(c.bounces.hard_bounces + c.bounces.soft_bounces), delta: `${formatNumber(c.bounces.hard_bounces)} hard, ${formatNumber(c.bounces.soft_bounces)} soft`, color: 'bad' },
      { label: 'Unsubscribes', value: formatNumber(c.unsubscribed), delta: `${formatPercent(c.unsubscribed / c.emails_sent)} of recipients`, color: 'warn' },
      { label: 'Abuse reports', value: formatNumber(c.abuse_reports), delta: `${formatPercent(c.abuse_reports / c.emails_sent)} of recipients`, color: 'bad' },
    ]
  }
  const size = getSegmentSize(selectedSegmentId.value as string)
  return [
    { label: 'Sent', value: formatNumber(s.emails_sent || 0), delta: `${formatPercent(s.emails_sent / c.emails_sent)} of the send`, color: 'muted' },
    { label: 'Opens', value: formatNumber(s.opens_total || 0), delta: `${formatNumber(s.unique_opens || 0)} unique, ${formatPercent(s.open_rate || 0)}`, color: (s.open_rate || 0) > (c.opens?.open_rate || 0) ? 'good' : 'warn' },
    { label: 'Clicks', value: formatNumber(s.clicks_total || 0), delta: `${formatNumber(s.unique_clicks || 0)} unique, ${formatPercent(s.click_rate || 0)}`, color: (s.click_rate || 0) > (c.clicks?.click_rate || 0) ? 'good' : 'warn' },
    { label: 'Bounces', value: formatNumber((s.hard_bounces || 0) + (s.soft_bounces || 0)), delta: `${formatNumber(s.hard_bounces || 0)} hard, ${formatNumber(s.soft_bounces || 0)} soft`, color: 'bad' },
    { label: 'Unsubscribes', value: formatNumber(s.unsubscribed || 0), delta: `${formatPercent((s.unsubscribed || 0) / (s.emails_sent || 1))} of the segment`, color: 'warn' },
    { label: 'Segment size', value: formatNumber(size), delta: `${formatPercent(size / getTotalListSize())} of the audience`, color: 'muted' },
  ]
})

// Segment rate against the whole send, as a relative difference.
const comparison = computed(() => {
  const c = campaignData.value?.campaign
  const s = campaignData.value?.segment_data
  const row = (label: string, seg: number, all: number) => ({
    label,
    up: seg > all,
    value: formatPercent(Math.abs((seg - all) / Math.max(all, 0.001))),
  })
  return [
    row('Open rate', s?.open_rate || 0, c?.opens?.open_rate || 0),
    row('Click rate', s?.click_rate || 0, c?.clicks?.click_rate || 0),
  ]
})

const topLinks = computed<any[]>(() => (campaignData.value?.metrics?.top_links || []).slice(0, 10))
const topDomains = computed<any[]>(() => (campaignData.value?.metrics?.top_domains || []).slice(0, 10))
const maxLinkClicks = computed(() => Math.max(0, ...topLinks.value.map((l) => l.total_clicks || 0)))

const ageText = computed(() => {
  const s = cacheStatus.value.age
  return s < 90 ? 'just now' : s < 5400 ? `${Math.round(s / 60)} minutes ago` : `${Math.round(s / 3600)} hours ago`
})

async function loadCampaignData(segmentId: string | null = null, refresh = false) {
  if (!props.campaignId || !props.groupAlias) {
    error.value = 'Missing campaign ID or group alias'
    loading.value = false
    return
  }
  if (segmentId && typeof segmentId === 'object') segmentId = null

  try {
    loading.value = true
    let url = `/api/v1/mailchimp/analytics/campaign/${props.campaignId}?groupAlias=${props.groupAlias}`
    if (segmentId) url += `&segmentId=${segmentId}`
    if (refresh) url += `&refresh=true`

    const response: any = await $fetch(url)
    if (response.success) {
      campaignData.value = response
      emit('loaded', response)
      cacheStatus.value = response._cache || { hit: false, age: 0, stale: false, fresh: true }
    } else {
      error.value = response.error || 'Failed to load campaign data'
    }
  } catch (err: any) {
    console.error('Error loading campaign data:', err)
    error.value = err?.data?.statusMessage || err?.message || 'An error occurred while loading campaign data'
  } finally {
    loading.value = false
    loadingSegmentData.value = false
  }
}

async function refreshData() {
  if (refreshingData.value) return
  refreshingData.value = true
  try {
    await loadCampaignData(selectedSegmentId.value, true)
  } finally {
    refreshingData.value = false
  }
}

function onSegmentChange(segmentId: any) {
  if (segmentId === selectedSegmentId.value) return
  if (typeof segmentId === 'object') return
  selectedSegmentId.value = segmentId
  loadingSegmentData.value = true
  loadCampaignData(segmentId)
}

function clearSegmentFilter() {
  selectedSegmentId.value = null
  loadingSegmentData.value = true
  loadCampaignData()
}

function getSegmentName(segmentId: string) {
  const segment = campaignData.value?.segments?.find((s: any) => s.id === segmentId)
  return segment ? segment.name : 'Unknown segment'
}
function getSegmentSize(segmentId: string) {
  const segment = campaignData.value?.segments?.find((s: any) => s.id === segmentId)
  return segment ? segment.member_count : 0
}
function getTotalListSize() {
  if (!campaignData.value?.campaign) return 1
  return campaignData.value.campaign.list_stats?.member_count || Math.max(campaignData.value.campaign.emails_sent, 1)
}

function formatNumber(value: any) {
  if (value === undefined || value === null) return '0'
  return new Intl.NumberFormat().format(value)
}
function formatPercent(value: any) {
  if (value === undefined || value === null) return '0%'
  if (typeof value === 'string' && value.includes('%')) return value
  return new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)
}
function formatDate(dateString: any, shortFormat = false) {
  if (!dateString) return ''
  if (shortFormat) {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  }
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}
function truncateUrl(url: string) {
  if (!url) return ''
  try {
    const urlObj = new URL(url)
    const path = urlObj.pathname
    const truncatedPath = path.length > 30 ? path.substring(0, 27) + '...' : path
    return `${urlObj.hostname}${truncatedPath}`
  } catch {
    return url.length > 50 ? url.substring(0, 47) + '...' : url
  }
}

// Reload if the campaign changes (e.g. switching emails without a full remount).
watch(() => [props.campaignId, props.groupAlias], () => {
  selectedSegmentId.value = null
  loadCampaignData()
})

onMounted(() => loadCampaignData())
</script>

<style scoped>
.ca-head {
  margin: 0;
  padding: 16px 20px;
  border-bottom: 1px solid var(--line);
  font-size: 18px;
  font-weight: 700;
}
.ca-facts {
  margin: 0;
}
.ca-facts > div {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  padding: 13px 20px;
}
.ca-facts > div + div {
  border-top: 1px solid var(--line);
}
.ca-facts dt {
  flex: 0 0 140px;
  color: var(--muted);
}
.ca-facts dd {
  flex: 1 1 240px;
  margin: 0;
  min-width: 0;
}
.ca-link {
  color: var(--acctext);
  font-weight: 600;
  min-width: 0;
}
.ca-link:hover {
  text-decoration: underline;
}
/* Six tiles: one row, or 3 by 2, or 2 by 3. Never five and an orphan. */
.ca-kpis {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 1100px) {
  .ca-kpis {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 560px) {
  .ca-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
.ca-pairs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(320px, 100%), 1fr));
  gap: 12px;
}
.ca-pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 6px;
}
.ca-num {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}
.ca-num--date {
  font-size: 16px;
  font-weight: 600;
}
.ca-empty {
  margin: 0;
  padding: 28px 20px;
  color: var(--muted);
}
.ca-linkrow {
  padding: 12px 20px;
}
.ca-linkrow + .ca-linkrow {
  border-top: 1px solid var(--line);
}
.ca-bar {
  height: 6px;
  margin-top: 8px;
  border-radius: 999px;
  background: var(--raise);
  overflow: hidden;
}
.ca-bar > div {
  height: 100%;
  border-radius: 999px;
  background: var(--accent);
}
.ca-table {
  width: 100%;
  min-width: 620px;
  border-collapse: collapse;
}
.ca-table th {
  padding: 10px 20px;
  color: var(--muted);
  font-size: 13px;
  font-weight: 500;
  text-align: right;
}
.ca-table td {
  padding: 12px 20px;
  border-top: 1px solid var(--line);
  text-align: right;
  white-space: nowrap;
}
.ca-table th:first-child,
.ca-table td:first-child {
  text-align: left;
}
.ca-table td span {
  color: var(--muted);
  font-size: 13px;
}
</style>
